import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalDialog, Input, Button, LinearProgress } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../fetch/fetch";

/**
 * 윈터 스쿨 등록 관리 > '결제링크 알림톡 발송' 팝업
 * 체크한 예약자들에게 결제 페이지(suneungsunbae.com/winterpay) 링크 알림톡('윈터스쿨 결제 당일 ver1' 템플릿)을 보낸다.
 *  - 템플릿 본문은 고정('오늘 10월 5일(월) 오후 8시부터 최종 등록 시작')이라 10/5 발송 전용. 변수는 지점·이름·버튼 링크 2개뿐
 *  - 여기서 입력하는 건 등록기한(= 결제링크 만료 시각) 하나. 서버가 링크 dueDate 로 저장하고 만료 알림·결제 차단에 쓴다
 *  - 이전 템플릿 '수능선배_윈터스쿨등록'은 아이템 요약정보 자리가 금액 형식만 받아(카카오 3079) 10/4 교체
 *  - POST /dashboard/payments/winterpay/sendlinks 는 즉시 batchId 를 돌려주고 서버가 백그라운드로 발송 → GET /sendbatch 를 2초마다 폴링해 진행률 표시
 *  - '성공'은 Solapi 접수 기준. 서버가 발송 뒤 20초·90초에 카카오 실제 전송 결과를 확인해 거절(3xxx)이면 실패로 바꾸므로, 확인이 끝날 때까지(unverified 0) 폴링을 이어간다
 *  - 같은 예약 행에 다시 보내면 같은 링크(URL)를 재사용하고 발송 횟수만 올라간다. 실패 건은 횟수에 들어가지 않는다.
 * props: open, onClose, targets({id,name,phoneNumber,location}[]), onDone()
 */

const toastOptions: any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored",
};

// datetime-local 값 → 서버 dueDate 'YYYY-MM-DD HH:mm'
const toServerDate = (v: string) => v.replace("T", " ").slice(0, 16);

const DEFAULT_DUE = "2026-10-06T20:00";
const FAQ_URL = "suneungsunbae.com/notificationRead?id=557";

// 템플릿 본문(고정) — 미리보기용. 서버 alimtalk.ts winterRegisterDayAlimtalk 와 같은 문구
const bodyPreview = (location: string, name: string) => `📢수능선배 ${location} 2027 윈터스쿨 최종 등록 안내

사전예약을 신청하신 ${name}님,
오늘 10월 5일(월) 오후 8시부터 2027 윈터스쿨 최종 등록이 시작됩니다.

사전예약 후 등록까지 완료하셔야 최종 등록이 확정되오니, 아래 내용을 확인하신 후 등록해주시기 바랍니다.

□ 2027 윈터스쿨 운영 일정
- 프리윈터 : 11/23(월) ~ 12/27(일)
- 정규윈터 : 12/28(월) ~ 2/28(일)
□ 최종 등록 방법 : 하단 [등록 신청] 버튼을 통해 등록 신청 진행

★ 프리윈터 혜택 : 희망 좌석 사전 신청 가능 (*11/23~12/12 입실자에 한함)
★ 할인 혜택
- 11/19 이전 등록 결제 시 프리윈터·정규윈터 5% 할인
- 설명회 당일 등록 결제 시 프리윈터·정규윈터 5% 할인

※ 사전예약은 윈터스쿨 등록 확정이 아니며, 등록 및 결제 완료 후 최종 등록이 확정됩니다.
※ 개강 전 취소 시 전액 환불됩니다.

[등록 신청] → suneungsunbae.com/winterpay?id=…&code=…
[윈터스쿨 자주 묻는 질문] → ${FAQ_URL}`;

const WinterSendLinkModal: React.FC<any> = (props) => {

    const [due, setDue] = useState<string>(DEFAULT_DUE);
    const [confirming, setConfirming] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [batch, setBatch] = useState<{ batchId: string, total: number, skipped: number } | null>(null);
    const [progress, setProgress] = useState<{ total: number, success: number, failed: number, pending: number, unverified: number } | null>(null);
    const timerRef = useRef<any>(null);

    const targets: any[] = props.targets || [];

    useEffect(() => {
        if (props.open) {
            setDue(DEFAULT_DUE);
            setConfirming(false);
            setLoading(false);
            setBatch(null);
            setProgress(null);
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [props.open]);

    const dueDateObj = new Date(due);
    const dueValid = !!due && !isNaN(dueDateObj.getTime()) && dueDateObj.getTime() > Date.now();
    const canSend = targets.length > 0 && dueValid;
    const first = targets[0];
    const previewLocation = first && first.location ? `${first.location}점` : "OO점";
    const previewName = first ? first.name : "OOO";

    // 진행률 폴링 — pending 이 0이 되면 멈춘다
    const startPolling = (batchId: string) => {
        if (timerRef.current) clearInterval(timerRef.current);
        const tick = async () => {
            try {
                const r: any = await getFetch(`/dashboard/payments/winterpay/sendbatch?batchId=${batchId}`);
                if (r.message === "success") {
                    setProgress({ total: r.total, success: r.success, failed: r.failed, pending: r.pending, unverified: r.unverified || 0 });
                    if (r.pending === 0 && (r.unverified || 0) === 0 && timerRef.current) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                    }
                }
            } catch (e) {
                console.log(e);
            }
        };
        tick();
        timerRef.current = setInterval(tick, 2000);
    };

    const send = async () => {
        if (!canSend || loading) return;
        try {
            setLoading(true);
            const result: any = await postFetch("/dashboard/payments/winterpay/sendlinks", {
                prereserveIds: targets.map((t) => t.id),
                dueDate: toServerDate(due),
            });
            if (result.message === "success") {
                setBatch({ batchId: result.batchId, total: result.total, skipped: result.skipped || 0 });
                if (result.total > 0) {
                    startPolling(result.batchId);
                } else {
                    setProgress({ total: 0, success: 0, failed: 0, pending: 0, unverified: 0 });
                }
            } else {
                console.log(result);
                toast.error(result.message === "wrongDate" ? "등록기한이 현재보다 뒤여야 합니다" : "발송 요청에 실패했습니다. 다시 시도해 주세요.", toastOptions);
                setConfirming(false);
            }
        } catch (e) {
            console.log(e);
            toast.error("발송 요청에 실패했습니다. 다시 시도해 주세요.", toastOptions);
            setConfirming(false);
        } finally {
            setLoading(false);
        }
    };

    const close = () => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (batch) props.onDone && props.onDone();
        props.onClose && props.onClose();
    };

    const sentAll = !!progress && progress.pending === 0;
    const done = sentAll && progress!.unverified === 0;
    const percent = progress && progress.total > 0 ? Math.round(((progress.success + progress.failed) / progress.total) * 100) : 0;

    const labelStyle: React.CSSProperties = { fontSize: "13px", marginBottom: "6px" };

    return (
        <Modal open={!!props.open} onClose={batch && !done ? undefined : close}>
            <ModalDialog>
                <div style={{ width: "420px", maxWidth: "92vw" }}>
                    <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: "6px" }}>
                        결제링크 알림톡 발송
                    </div>
                    <div style={{ fontSize: "13px", color: "#6B7684", marginBottom: "18px" }}>
                        선택한 {targets.length}명에게 '윈터스쿨 결제 당일 ver1' 알림톡을 보냅니다. 버튼 '등록 신청'으로 결제 페이지에 들어갑니다.
                    </div>
                    <div style={{ fontSize: "12px", color: "#E8505B", fontWeight: 600, marginBottom: "14px" }}>
                        본문에 '오늘 10월 5일(월) 오후 8시부터'가 고정돼 있어 10/5 발송 전용입니다. 다른 날 보내면 안 됩니다.
                    </div>

                    {!batch && (
                        <>
                            <div style={labelStyle}>등록기한 (= 결제링크 만료 시각 — 알림톡 본문엔 안 나가고, 링크 만료·만료 1시간 전 알림에 쓰임)</div>
                            <Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} sx={{ marginBottom: "14px" }} disabled={confirming} />

                            <div style={labelStyle}>알림톡 미리보기 <span style={{ color: "#8B95A1" }}>(지점·이름은 첫 번째 대상 기준, 본문은 템플릿 고정)</span></div>
                            <div style={{ background: "#F5F6F8", borderRadius: "8px", padding: "12px 14px", fontSize: "12px", lineHeight: 1.55, color: "#333D4B", whiteSpace: "pre-wrap", marginBottom: "6px", maxHeight: "260px", overflow: "auto" }}>
                                {bodyPreview(previewLocation, previewName)}
                            </div>
                            {!dueValid && <div style={{ fontSize: "12px", color: "#E8505B", marginBottom: "6px" }}>등록기한은 현재 시각보다 뒤여야 합니다.</div>}

                            {confirming && (
                                <div style={{ fontSize: "13px", color: "#E8505B", fontWeight: 600, marginTop: "10px" }}>
                                    {targets.length}명에게 실제로 알림톡이 발송됩니다. 정말 보낼까요?
                                </div>
                            )}

                            <div style={{ marginTop: "20px", display: "flex", gap: "8px" }}>
                                <Button variant="outlined" color="neutral" fullWidth onClick={confirming ? () => setConfirming(false) : close} disabled={loading}>
                                    {confirming ? "아니오" : "취소"}
                                </Button>
                                {confirming ? (
                                    <Button variant="solid" color="danger" fullWidth onClick={send} loading={loading} disabled={!canSend}>
                                        네, {targets.length}명에게 발송
                                    </Button>
                                ) : (
                                    <Button variant="solid" fullWidth onClick={() => setConfirming(true)} disabled={!canSend}>
                                        발송하기 ({targets.length}명)
                                    </Button>
                                )}
                            </div>
                        </>
                    )}

                    {batch && (
                        <>
                            <div style={{ fontSize: "14px", marginBottom: "10px" }}>
                                {done ? "발송이 끝났습니다." : sentAll ? "카카오 전송 결과를 확인하는 중입니다 (최대 1분 30초). 창을 닫지 말고 잠시 기다려 주세요." : "발송 중입니다. 창을 닫지 말고 잠시 기다려 주세요."}
                            </div>
                            <LinearProgress determinate value={done ? 100 : percent} sx={{ marginBottom: "12px" }} />
                            <div style={{ display: "flex", gap: "16px", fontSize: "13px", color: "#333D4B", marginBottom: "6px" }}>
                                <span>대상 {batch.total}명</span>
                                <span style={{ color: "#1B64DA" }}>성공 {progress ? progress.success : 0}</span>
                                <span style={{ color: "#E8505B" }}>실패 {progress ? progress.failed : 0}</span>
                                <span style={{ color: "#6B7684" }}>대기 {progress ? progress.pending : batch.total}</span>
                                {sentAll && !done && <span style={{ color: "#6B7684" }}>결과 확인 중 {progress!.unverified}</span>}
                            </div>
                            {batch.skipped > 0 && (
                                <div style={{ fontSize: "12px", color: "#6B7684" }}>번호 형식이 맞지 않아 건너뛴 행 {batch.skipped}건 (010으로 시작하는 11자리만 발송)</div>
                            )}
                            <div style={{ fontSize: "12px", color: "#6B7684", marginTop: "4px" }}>'성공'은 카카오 수신까지 확인된 건입니다(거절되면 실패로 바뀜). 실패 건은 발송 횟수에 들어가지 않으며, 표의 '결제링크 발송시각' 숫자를 눌러 건별 사유를 볼 수 있어요.</div>

                            <div style={{ marginTop: "20px", display: "flex", gap: "8px" }}>
                                <Button variant="solid" fullWidth onClick={close} disabled={!done}>
                                    {done ? "닫기" : sentAll ? "결과 확인 중..." : "발송 중..."}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default WinterSendLinkModal;
